import { useCallback } from "react";
import { getRunState } from "../store/runStore.js";
import { canEdit } from "../lib/forecast/sharedRowGuards.js";
import { makeDefaultDeps } from "../commands/deps.js";
import { classifyChatIntent } from "../commands/classifyChatIntent.js";
import { makeEntry } from "../lib/forecast/model.js";
import { selectBranchRuns } from "../lib/forecast/selectors.js";
import { answerSummary, answerText } from "../lib/forecast/answerSummary.js";
import { SERIES_PALETTE } from "../lib/colors.js";
import { SCALE_PRESETS } from "../lib/timeline/geometry.js";
import { resolveEngine, engineLabel } from "../lib/forecast/engines.js";
import {
  isLikelyForecastQuestion,
  looksLikeForecastIntent,
  looksLikeMultiForecastIntent,
  isAffirmativeReply,
  isNegativeReply,
} from "../lib/questionIntent.js";
import { abortAllPending } from "../lib/util/abort.js";
import { isMultiwayPrefixed, isBinaryPrefixed } from "../lib/forecast/multiway.js";
import { forecastReportContext } from "../lib/forecast/reportContext.js";
import { forecastDistributionContext } from "../lib/forecast/distributionContext.js";
import { recentConversationText } from "../lib/forecast/conversationMemory.js";
import { resolveTriggerInstant } from "../lib/forecast/branchTrigger.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { userFacingError } from "../lib/forecast/runErrorText.js";
import { beginChatTurn, currentChatTurn, newMessageId } from "../lib/chatTurn.js";
import { captureChatTarget } from "../lib/forecast/chatTarget.js";
import { beginChatAsk } from "../lib/telemetry/chatAsk.js";
import { legacyText, recordLegacyEvent, recordTextEvent } from "../lib/telemetry/groups.js";

// The intents that WRITE to the active forecast — each mints a branch on its
// tree. Every other intent is navigation, explanation, or the viewer's own
// client-side state. A named set at module scope so adding a mutating intent
// means deciding whether it belongs here, rather than silently inheriting
// "allowed".
export const SHARED_WRITE_INTENTS = new Set(["add_event", "branch_occurs", "branch_not_occurs"]);

/**
 * True iff this intent would write to a forecast the viewer may not edit.
 *
 * KEYED ON `canEdit`, NOT ON OWNERSHIP, and the difference is the whole point:
 * an in-org collaborator does not own the forecast and MAY branch it — "branches
 * are shared across all collaborators" — while an out-of-org one may not, and is
 * offered their own copy instead. Gating on `isOwn` would refuse the colleague
 * the server is about to accept, which is the mirror of the bug this phase keeps
 * finding: a control that silently does nothing, inverted into a refusal that
 * did not need to happen.
 *
 * The answer comes from the server (`canEdit` on the shared wire, from the same
 * `canEditSharedForecast` the branch routes enforce), via `sharedRowGuards`,
 * which supplies the fail-safe default for a row predating the field.
 *
 * Exported as a predicate so the rule is testable without widening the hook's
 * returned API (which is `{ handleChatMessage }` and should stay that).
 *
 * @param {string} intent
 * @param {{canEdit?: boolean}|null|undefined} active
 * @returns {boolean}
 */
export const refusesSharedWrite = (intent, active) =>
  SHARED_WRITE_INTENTS.has(intent) && !canEdit(active);

// Intents that produce a VISIBLE UI change in dispatchIntent — the only ones for which a
// bare "Done." confirmation makes sense when the model returns no reply text. "explain" and
// any unknown intent are conversational/no-op and get a re-ask instead (#3535).
const ACTION_INTENTS = new Set([
  "add_event",
  "branch_occurs",
  "branch_not_occurs",
  "add_index",
  "remove_index",
  "change_indices",
  "zoom_out",
  "zoom_to_timeline",
  "center",
  "set_detail",
  "show_report",
  "reset",
  "set_engine",
  "open_forecast_manager",
  "file_bug",
  "open_settings",
  "open_account",
  "open_series_panel",
  "close_panels",
]);

/**
 * Sayer chat-intent domain (EPIC #157, phase P5.4). The pure LLM classifier moved to
 * `commands/classifyChatIntent.ts`; this hook keeps the parts that MUST stay in React —
 * `dispatchIntent` (the UI command-router: zoom/series/report/engine/branch/submit),
 * `classifyAndDispatch` (assemble context → classify → route), and `handleChatMessage`
 * (the chat entry point + tour state machine). It calls the command for classification and
 * reads the live store via `getRunState()` (no `runStateRef`/`dispatchRun` args threaded).
 *
 * Because the hook is called AFTER `useForecastSubmit`/`useBranchForecast`, it receives
 * `submit`/`handleBranch`/`handleCustomEventBranch` directly — the old `submitRef`
 * forward-ref (needed only because `dispatchIntent` was declared before `submit`) dissolves.
 *
 * Returns `{ handleChatMessage }` — the external API (ChatContent onSubmit, Enter, voice).
 */
export function useChatIntent(deps) {
  const {
    // state
    active,
    series,
    input,
    forecastEngine,
    allEngines,
    multiwayEnabled,
    autoQuestionTypeEnabled,
    // ops (from the sibling command-bound hooks)
    submit,
    handleBranch,
    handleCustomEventBranch,
    // UI handlers
    addSeries,
    removeSeries,
    jumpTo,
    centerToday,
    openReport,
    resetForecast,
    setDetailLevel,
    setForecastEngine,
    appendEntry,
    fitTimeline,
    answerChat,
    // The chat outside the current forecast, as prompt text (useChatJournal). Optional.
    earlierConversation,
    // How-to text from the first-contact tips ("" when they are off). Optional.
    howSayerWorks = "",
    // panel/overlay actions the chat can drive (open forecast manager / bug report /
    // settings / account / series panel / close everything) — thin closures from App.jsx.
    panels,
    // setters
    setChatThinking,
    setInput,
    setTourExamples,
    setTourFixture,
    pushSayerMsg,
    pushUserMsg,
    // refs (stable)
    pendingEngineRevertRef,
    pendingAbortsRef,
    pendingNewForecastRef,
    tourRef,
    tourEndConfirmRef,
    exampleRevealTimerRef,
    forecastNextRef,
    postTourTipRef,
    ninetyNotePendingRef,
    contractNotePendingRef,
  } = deps;

  const dispatchIntent = useCallback(
    // `turn`: the chat turn that produced this intent (lib/chatTurn.js), or null. Only an
    // intent that hands off to submit (an engine rerun) uses it, to pass it on.
    // `ownerRunId`: the forecast the message was sent on, captured before the classifier
    // await — a trigger it asks for branches that forecast, wherever the selection is now.
    // `target`: where the turn's lines are filed, captured with it (lib/forecast/chatTarget.ts).
    (intent, params, turn, ownerRunId, target) => {
      const events = active?.events || [];
      // ── WHAT A RECIPIENT MAY ASK FOR, AND WHAT THEY MAY NOT ────────────────
      //
      // This guard used to sit at the top of `handleChatMessage` and refuse the
      // whole turn (`if (active?.isOwn === false) return;`), which was right when
      // a recipient had no chat at all. Now that every holder has their own
      // thread, refusing the turn would refuse the QUESTION — the thing chat is
      // for — over the handful of intents that actually write.
      //
      // So the gate moved to the writes. These three MINT A BRANCH on the active
      // forecast, which for a recipient is someone else's tree; non-owner branch
      // creation is its own step of the design and its server side does not exist
      // yet, so a recipient asking for one would otherwise get a branch that
      // appears locally and is never persisted.
      //
      // It SAYS SO rather than returning silently — a question that produces
      // nothing at all is the failure shape this whole phase has been removing.
      // Everything else here is navigation, explanation, or the viewer's own
      // client-side state, none of which touches the owner's row.
      if (refusesSharedWrite(intent, active)) {
        appendEntry(
          makeEntry({
            role: "assistant",
            content:
              "This forecast belongs to another organization, so I can't add a scenario to it here. Ask me anything about it — or forecast the question yourself, then explore scenarios on your copy.",
          }),
          { target },
        );
        return;
      }
      switch (intent) {
        case "add_event": {
          const desc = params.description || params.event || "";
          const day =
            params.date || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
          // A day NAMED in chat, never pointed at — so there is no click instant to carry
          // and resolveTriggerInstant anchors it at midday rather than the UTC midnight a
          // bare day string decays to.
          // Quick, as from the trigger box, unless the message asked for Deep.
          const tier = /deep/i.test(String(params.engine || params.depth || "")) ? "deep" : "quick";
          if (desc)
            handleCustomEventBranch(desc, resolveTriggerInstant({ day }) || day, {
              ownerRunId,
              tier,
            });
          return;
        }
        case "branch_occurs":
        case "branch_not_occurs": {
          const title = (params.eventTitle || params.event || "").toLowerCase();
          const ev = events.find((e) => (e.title || "").toLowerCase().includes(title));
          if (ev) handleBranch(ev, "probability", intent === "branch_occurs" ? 1.0 : 0.0);
          return;
        }
        case "add_index": {
          const name = params.name || params.index || "";
          if (name) {
            const usedColors = Object.values(series).map((s) => s.color);
            const c = SERIES_PALETTE.find((x) => !usedColors.includes(x)) || SERIES_PALETTE[0];
            addSeries(name, c);
          }
          return;
        }
        case "remove_index": {
          const name = params.name || params.index || "";
          if (name) {
            const k = Object.keys(series).find((s) => s.toLowerCase().includes(name.toLowerCase()));
            if (k) removeSeries(k);
          }
          return;
        }
        case "change_indices": {
          const names = params.names || [];
          Object.keys(series).forEach((k) => removeSeries(k));
          names.forEach((n, i) =>
            setTimeout(() => addSeries(n, SERIES_PALETTE[i % SERIES_PALETTE.length]), i * 100),
          );
          return;
        }
        case "zoom_out":
          jumpTo(SCALE_PRESETS[0].ppd);
          return;
        case "zoom_to_timeline":
          fitTimeline();
          return;
        case "center":
          centerToday();
          return;
        case "set_detail": {
          const lv = Math.max(1, Math.min(5, parseInt(params.level) || 3));
          setDetailLevel(lv);
          return;
        }
        case "show_report":
          openReport(true);
          return;
        // Panel/overlay controls (Sayer "open the forecast manager", "file a bug", …).
        // Guarded with `panels?.` so a caller that doesn't wire the bundle degrades to a
        // no-op rather than throwing (the response text still posts).
        case "open_forecast_manager":
          panels?.openForecastManager?.();
          return;
        case "file_bug":
          panels?.openBugReport?.();
          return;
        case "open_settings":
          panels?.openSettings?.();
          return;
        case "open_account":
          panels?.openAccount?.();
          return;
        case "open_series_panel":
          panels?.openSeriesPanel?.();
          return;
        case "close_panels":
          panels?.closeAllPanels?.();
          return;
        case "reset":
          resetForecast();
          return;
        case "set_engine": {
          const engine = resolveEngine(params.engine || params.name || "", allEngines);
          if (!engine) {
            appendEntry(
              makeEntry({
                role: "assistant",
                content: allEngines.length
                  ? `I don't recognize that depth. You can choose ${[...new Set(allEngines.map((e) => engineLabel(e)))].join(" or ")}.`
                  : "Sayer is still connecting. Try choosing a depth again in a moment.",
              }),
              { target },
            );
            return;
          }
          const persistent = params.persistent === true;
          // Non-persistent swap: remember the prior engine so doForecast can restore it once the run finishes.
          if (!persistent && forecastEngine && forecastEngine !== engine)
            pendingEngineRevertRef.current = forecastEngine;
          else pendingEngineRevertRef.current = null;
          setForecastEngine(engine);
          // If the user wants to re-run the current question with the new engine, pass
          // `engine` directly to submit — setForecastEngine above won't be visible in
          // submit's closure on this same call. (submit is in scope directly now — the
          // old submitRef forward-ref dissolved with the command extraction.)
          if (params.rerun && active?.question) {
            turn?.route("submit");
            submit(active.question, engine, false, turn);
          }
          return;
        }
        case "explain":
        default:
          return; // no UI action
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      active,
      series,
      handleCustomEventBranch,
      handleBranch,
      addSeries,
      removeSeries,
      jumpTo,
      centerToday,
      fitTimeline,
      openReport,
      resetForecast,
      setDetailLevel,
      allEngines,
      forecastEngine,
      appendEntry,
      setForecastEngine,
      submit,
      panels,
    ],
  );

  const classifyAndDispatch = useCallback(
    async (q, ownerRunId = active?.id ?? null) => {
      // Captured once: a later message opens a NEW turn, and this one must not report
      // into it. Null when called outside a timed chat turn — every use is optional.
      const turn = currentChatTurn();
      // Where every reply below is filed: the forecast the message was sent on, not whatever
      // is selected when the classifier answers.
      const target = turn?.target ?? captureChatTarget(getRunState());
      setChatThinking(true);
      try {
        // Build context for the classifier (assembled from the live store + series).
        const c = active.contract || {};
        // Triggers now carry their one-line description too, so "what's driving this?" /
        // "tell me about that trigger" have something to reference (not just a title + %).
        const evtList = (active.events || [])
          .map((e) => {
            const desc = (e.description || "").trim().replace(/\s+/g, " ").slice(0, 160);
            const head = `- ${e.title} (${e.date}, direction:${e.direction || "neutral"}, ${Math.round((e.p_event || 0.5) * 100)}%)`;
            return desc ? `${head}\n    ${desc}` : head;
          })
          .join("\n");
        // The report's reasoning + sources — so "why 63%?" / "strongest Yes driver?" /
        // "which sources?" can be answered from the actual analysis (see reportContext). This
        // is the FULL report now, not the old 3-reason clip.
        const rc = forecastReportContext(active);
        // The distribution half. Returns isDistribution:false for a binary forecast, and the
        // prompt branches on that — so the two are never both rendered and a multi run stops
        // being described as a bare (and meaningless) P(YES). See distributionContext.
        const dc = forecastDistributionContext(active);
        // Branch RUN objects from the store; probability → contract?.probability.
        const brList = selectBranchRuns(getRunState(), active?.id)
          .map((b) => `- ${b.modification} → ${answerText(answerSummary(b)) ?? "no answer yet"}`)
          .join("\n");
        const idxList = Object.keys(series).join(", ") || "none";
        // The SHARED transcript builder, not a local re-implementation: this had its own
        // copy of the same active-run-else-draft rule and its own hardcoded 10, so widening
        // the window (or capping a 32 KB pasted message) in conversationMemory.js silently
        // did not apply to the classifier — the one path that most needs the referent.
        const recentMsgs = recentConversationText(getRunState(), { target });
        const context = {
          contractQuestion: c.question || c.title || "",
          probabilityPct: Math.round((c.probability || 0) * 100),
          forecastAnswer: answerText(answerSummary(active)),
          resolutionDate: c.resolution_date || "unknown",
          eventsList: evtList,
          branchesList: brList,
          indicesList: idxList,
          currentEngineLabel: engineLabel(forecastEngine),
          recentMessages: recentMsgs,
          earlierConversation: (await earlierConversation?.(ownerRunId, q)) || "",
          reportSummary: rc.summary,
          reportRephrased: rc.rephrased,
          reportCriteria: rc.criteria,
          reasonsFor: rc.reasonsFor,
          reasonsAgainst: rc.reasonsAgainst,
          reportAggregation: rc.aggregation,
          reportCalibration: rc.calibration,
          reportEvidence: rc.evidence,
          sourcesList: rc.sources,
          howSayerWorks,
          dist: dc,
          // Always on: the prompt and the transport read this one literal, so they
          // cannot disagree about whether the tool exists.
          webSearch: true,
        };

        const ac = new AbortController();
        pendingAbortsRef.current.add(ac);
        try {
          // The LLM classification is the one I/O core — extracted to the command.
          const classifyDone = turn?.phase("classify");
          const { parsed, raw } = await classifyChatIntent(makeDefaultDeps(), {
            question: q,
            context,
            signal: ac.signal,
            turnId: turn?.id ?? null,
          });
          classifyDone?.();
          if (ac.signal.aborted) {
            setChatThinking(false);
            turn?.finish("aborted");
            return;
          }
          if (parsed && parsed.intent) {
            recordUiEvent({ event: "ui.chat.intent", intent: parsed.intent });
            recordTextEvent({
              event: "chat.intent",
              intent: parsed.intent,
              msg_id: turn?.msgId ?? null,
              chat_turn_id: turn?.id ?? null,
            });
            if (parsed.intent === "confirm_new_forecast" && parsed.params?.question) {
              // Stash the question and let handleChatMessage resolve the user's next
              // reply deterministically (see pendingNewForecastRef) — the classifier
              // does NOT get a second turn to re-derive it, which is what broke on
              // short confirmations like "new one".
              setChatThinking(false);
              pendingNewForecastRef.current = parsed.params.question;
              pushSayerMsg(
                parsed.response ||
                  `It sounds like a new forecast — want me to run one on "${parsed.params.question}"? (yes / no)`,
                { target },
              );
              turn?.finish("confirm_prompt", { intent: parsed.intent });
              return;
            }
            if (parsed.intent === "new_forecast" && parsed.params?.question) {
              setChatThinking(false);
              // If the LLM passed an engine, resolve it and either set permanently or schedule a one-shot revert.
              const engineParam = parsed.params.engine
                ? resolveEngine(parsed.params.engine, allEngines)
                : null;
              if (engineParam) {
                const persistent = parsed.params.persistent === true;
                if (!persistent && forecastEngine && forecastEngine !== engineParam)
                  pendingEngineRevertRef.current = forecastEngine;
                else pendingEngineRevertRef.current = null;
                setForecastEngine(engineParam);
              }
              turn?.route("submit");
              submit(parsed.params.question, engineParam || undefined, false, turn);
              return;
            }
            dispatchIntent(parsed.intent, parsed.params || {}, turn, ownerRunId, target);
            // "explain" (and any unknown intent) performs NO UI action, so a bare "Done."
            // reads as a shrug when the model returned no reply text (#3535). Only the
            // action intents get "Done." as a confirmation; a conversational intent with no
            // text falls back to a re-ask instead of pretending something happened.
            const isConversational =
              parsed.intent === "explain" || !ACTION_INTENTS.has(parsed.intent);
            pushSayerMsg(
              parsed.response ||
                (isConversational ? "Could you rephrase that? I didn't quite catch it." : "Done."),
              { target },
            );
            // An intent that handed off to submit (an engine rerun) re-routed the turn;
            // submit closes it when the rerun's contract shows.
            if (turn?.routeName !== "submit") {
              turn?.finish(isConversational ? "explained" : "dispatched", {
                intent: parsed.intent,
              });
            }
          } else {
            recordUiEvent({ event: "ui.chat.intent", intent: "unparsed" });
            pushSayerMsg(raw || "I couldn't understand that. Try rephrasing?", { target });
            turn?.finish("unparsed");
          }
        } finally {
          pendingAbortsRef.current.delete(ac);
        }
      } catch (e) {
        if (e.name === "AbortError") {
          setChatThinking(false);
          turn?.finish("aborted");
          return;
        }
        recordUiEvent({ event: "ui.chat.error", message: String(e.message || e).slice(0, 300) });
        console.error("[Sayer] chat request failed:", e);
        pushSayerMsg(
          userFacingError(e, "Sayer couldn't answer just now. Try asking again in a minute."),
          { target },
        );
        turn?.finish("error");
      }
      setChatThinking(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      active,
      series,
      submit,
      dispatchIntent,
      allEngines,
      forecastEngine,
      pushSayerMsg,
      setForecastEngine,
      howSayerWorks,
      // Read from the render closure inside — omitting it would pin the first value the
      // hook ever saw, so flipping the debug checkbox would not reach the next chat turn.
    ],
  );

  // Thin entry point: append the user's message, then classify + dispatch.
  // Kept as the external API (ChatContent onSubmit, Enter-to-submit, voice).
  const handleChatMessage = useCallback(
    async (overrideText, { origin = null, surface = undefined } = {}) => {
      // Fail-closed handler guard (Task 2.5) — the composer is ABSENT (not
      // disabled) in the UI for a shared active row (ChatContent's own
      // `readOnly` render gate, keyed on the same `isOwn` field — see its
      // `isReadOnlyRow(forecast)` check), so this is normally unreachable.
      // Kept here anyway, as the FIRST check before any other branch
      // (tour/multiway/binary/etc.):
      // "unreachable today" is exactly the reasoning that let several of
      // this phase's other findings through once a caller changed — a
      // re-mounted composer, a voice-input path, or a future refactor that
      // stops checking ownership before rendering must still hit a no-op
      // here, not a live write against someone else's forecast.
      // `active?.isOwn` defaults `true` for every pre-existing owner-leg run
      // (model.ts), so this only ever no-ops for a genuinely shared active row.
      //
      // FAIL-OPEN ON A MISSING FIELD, BY A DEPENDENCY ELSEWHERE, NOT BY
      // CONSTRUCTION: this checks `=== false`, so `active.isOwn === undefined`
      // reads as owned (correct — an owner-leg run predating this field must
      // never be treated as shared). That's safe ONLY because
      // `hydrate.js`'s `rowToRun` sets `run.isOwn = row.isOwn ?? true` —
      // `undefined` is unreachable through the real hydration path — plus
      // the server enforcing independently regardless of what this guard
      // does. Same shape as `toSharedWire`'s subtractive field-drop (see its
      // own header): safe by a dependency in another file, not by this
      // line alone. Anyone editing `hydrate.js`'s default is touching a
      // security-relevant dependency of this guard, not just a display default.
      //
      // Same principle as selectCancellableRun's own guard (selectors.ts):
      // read-only means the underlying selection is inert, not just that a
      // button is missing.
      const q = (typeof overrideText === "string" ? overrideText : input).trim();
      const T = tourRef.current;
      if (T.tourActive) {
        // Mid-tour: empty Enter advances a "next"/"continue" step.
        if (!q) {
          if (T.tourWaitKey === "next" || T.tourWaitKey === "continue")
            T.signalTourAction?.(T.tourWaitKey);
          return;
        }
        // Awaiting a yes/no to "end the tour and run that forecast?"
        if (tourEndConfirmRef.current) {
          const pendingQ = tourEndConfirmRef.current;
          tourEndConfirmRef.current = null;
          setInput("");
          if (/^(no|nope|nah|not now|keep going|stay|continue|n)\b/i.test(q)) {
            pushUserMsg(q);
            pushSayerMsg("No problem — let's keep going.", {
              kind: "tour",
              target: captureChatTarget(getRunState()),
            });
            return;
          }
          // Anything affirmative (or another forecast-like ask) ends the tour and runs it.
          T.endTour?.("interrupted");
          // Affirmative ("yes") → show the reply, then run the pending question. A fresh
          // forecast-like ask → submit() pushes it itself, so don't pushUserMsg(q) too
          // (that would duplicate the question in the chat).
          const affirmative = /^(yes|yep|yeah|sure|ok|okay|y|end|end tour|do it|go)\b/i.test(q);
          if (affirmative) pushUserMsg(q);
          submit(affirmative ? pendingQ : q);
          return;
        }
        setInput("");
        pushUserMsg(q);
        if (isLikelyForecastQuestion(q)) {
          // Looks like a forecast request — confirm before bailing out of the tour.
          tourEndConfirmRef.current = q;
          pushSayerMsg(
            "That looks like a forecast you'd like to run. Want to end the tour and forecast it? (yes / no)",
            { kind: "tour", target: captureChatTarget(getRunState()) },
          );
          return;
        }
        // Otherwise it's a question about Sayer — answer it as a tour aside and keep
        // the tour going (the current step's wait stays armed).
        T.answerTourAside?.(q);
        return;
      }
      if (!q) return;
      // Time this message to the moment its result is on screen (lib/chatTurn.js). Tour
      // turns above are scripted and deliberately not timed. Each branch below names
      // itself; a branch that hands off to submit() routes to "submit", and submit closes
      // the turn when the preprocessed contract shows.
      const turn = beginChatTurn();
      turn.msgId = newMessageId();
      // The forecast (or draft) this message is sent on, fixed now: every line the turn writes
      // later — after the classifier, the answer, a submit's own checks — lands there.
      turn.target = captureChatTarget(getRunState(), { msgId: turn.msgId });
      // The text the id belongs to: submit reuses the id only for this same text (an
      // engine rerun or a confirmed pending question persists a different one).
      turn.msgText = q;
      // An example question arrives as `overrideText`; a forecast it starts is from `example`.
      // A caller that names its own `origin` (the new-question page's Ask) sends typed text.
      turn.fromExample = typeof overrideText === "string" && !origin;
      turn.origin = origin || (turn.fromExample ? "chat.example-question" : "chat.send");
      beginChatAsk(turn, { forecastId: active?.id ?? null, origin: turn.origin, surface });
      // Every non-tour path below starts here, so one chat.send covers the MW: / B: /
      // post-tour shortcuts as well as the classified ones.
      recordTextEvent({
        event: "chat.send",
        text: q,
        msg_id: turn.msgId,
        chat_turn_id: turn.id,
        has_active: !!active,
        ...(active ? { forecast_id: active.forecastId ?? null } : { draft: true }),
      });
      // Multi-value ("multiway") mode: a leading `MW:` (flag-gated) is an UNAMBIGUOUS
      // forecast request — route it straight to submit, bypassing the LLM intent
      // classifier (which might mis-handle the control prefix). submitForecast strips the
      // prefix and drives the multi-value path. Abort in-flight branches + force past the
      // loading guard, exactly like the isLikelyForecastQuestion shortcut below.
      if (multiwayEnabled && isMultiwayPrefixed(q)) {
        setInput("");
        abortAllPending(pendingAbortsRef);
        turn.route("submit");
        submit(q, undefined, true, turn);
        return;
      }
      // A leading `B:` is the symmetric force-BINARY override — likewise an UNAMBIGUOUS
      // forecast request, so route it straight to submit (which strips the prefix and pins
      // the plain binary path). Flag-independent: forcing binary is always safe, and this
      // guarantees the control prefix is not mis-handled by the intent classifier.
      if (isBinaryPrefixed(q)) {
        setInput("");
        abortAllPending(pendingAbortsRef);
        turn.route("submit");
        submit(q, undefined, true, turn);
        return;
      }
      // Any real message past the tour gates retires the post-skip example list —
      // once the user forecasts (clicks an example or types their own), those
      // yellow questions must go away like a standard forecast, not linger. Cancel
      // the pending reveal timer too (armed on tour skip/end) so it can't fire ~2s
      // later and re-show the list this message just hid.
      if (exampleRevealTimerRef.current) {
        clearTimeout(exampleRevealTimerRef.current);
        exampleRevealTimerRef.current = null;
      }
      setTourExamples(false);
      // Right after the tour, the user was told to "ask your own question" — so
      // forecast it directly instead of routing through the LLM classifier (which
      // would ask whether they want a new forecast vs. discuss the tour's one).
      // Clear the example chips and arm the post-forecast tip (replay + bug icon).
      if (forecastNextRef.current) {
        forecastNextRef.current = false;
        setTourExamples(false);
        // Belt-and-suspenders: the tour fixture override is normally cleared by
        // endTour, but clear it here too so the user's own forecast can never take
        // the fixture short-circuit (which skips the real streaming path and its
        // progress-% ticks). Their question must run a genuine server forecast.
        setTourFixture(null);
        // The draft intro persists across the tour: submit()'s draft-adoption path
        // (`draftAdopted` in commands/submitForecast.ts) carries
        // any pre-tour intro into the user's first real forecast thread and then
        // clears the draft. A redundant clear here would discard a legitimate intro
        // that the user typed before the tour started.
        postTourTipRef.current = true;
        ninetyNotePendingRef.current = true;
        contractNotePendingRef.current = true;
        setInput("");
        // Abort any in-flight (tour) branches so their global `loading` can't drop
        // this forecast, then force past the loading guard.
        abortAllPending(pendingAbortsRef);
        turn.route("submit");
        submit(q, undefined, true, turn);
        return;
      }
      setInput("");
      recordLegacyEvent({ event: "ui.chat.send", text: legacyText(q), has_active: !!active });
      // Awaiting a yes/no to the classifier's "run this as a NEW forecast?" prompt
      // (pendingNewForecastRef, set on the confirm_new_forecast intent). Resolve it
      // HERE, deterministically — do NOT send the reply back through the LLM (that
      // fumbled "new one": it re-greeted or sat there). Affirmative → submit the
      // stashed question; negative → drop it and stay on the current forecast;
      // neither → treat the message as brand-new and fall through to normal routing.
      if (pendingNewForecastRef.current) {
        const pendingQ = pendingNewForecastRef.current;
        if (isAffirmativeReply(q)) {
          pendingNewForecastRef.current = null;
          pushUserMsg(q, { msgId: turn.msgId, target: turn.target });
          abortAllPending(pendingAbortsRef);
          turn.route("submit");
          submit(pendingQ, undefined, true, turn);
          return;
        }
        if (isNegativeReply(q)) {
          pendingNewForecastRef.current = null;
          pushUserMsg(q, { msgId: turn.msgId, target: turn.target });
          pushSayerMsg("Got it — I'll stay on the current forecast. What would you like to know?", {
            target: turn.target,
          });
          turn.route("confirm_reply").finish("declined");
          return;
        }
        // Neither yes nor no — the user moved on; drop the pending confirmation and
        // process this message normally below.
        pendingNewForecastRef.current = null;
      }
      // No active forecast: only run a forecast if the message actually reads like
      // one (yes/no question). General questions ("what does Sayer do?") get a chat
      // answer instead of submit()'s "needs a timeframe" rejection.
      if (!active) {
        // With auto question-type routing on, a multi-outcome question ("how many /
        // which / what will …" with a timeframe) is ALSO a forecast — otherwise the
        // binary-only looksLikeForecastIntent would deflect it to chat before it ever
        // reaches submit → /preprocess/auto.
        if (
          looksLikeForecastIntent(q) ||
          (autoQuestionTypeEnabled && looksLikeMultiForecastIntent(q))
        ) {
          turn.route("submit");
          submit(q, undefined, false, turn);
          return;
        }
        pushUserMsg(q, { msgId: turn.msgId, target: turn.target });
        turn.route("preforecast_chat");
        // answerChat resolves to what the user saw ("answered" / "aborted" / "error"), or
        // hands off to submit() — re-routing the turn, which submit then closes. Only a
        // turn still answering here is closed when it returns.
        Promise.resolve(answerChat(q)).then(
          (outcome) => {
            if (turn.routeName === "preforecast_chat") turn.finish(outcome || "answered");
          },
          () => turn.finish("error"),
        );
        return;
      }
      // A standalone yes/no question with a timeframe is unambiguously a NEW
      // forecast — and every example question is one — so submit it directly
      // rather than routing to the LLM classifier (which would otherwise ask
      // whether to forecast it vs. fold it into the active forecast). Abort any
      // in-flight branches first and force past the loading guard so running
      // branches can't silently drop the new forecast.
      if (
        isLikelyForecastQuestion(q) ||
        (autoQuestionTypeEnabled && looksLikeMultiForecastIntent(q))
      ) {
        abortAllPending(pendingAbortsRef);
        turn.route("submit");
        submit(q, undefined, true, turn);
        return;
      }
      pushUserMsg(q, { msgId: turn.msgId, target: turn.target });
      turn.route("classify");
      // The forecast this message was sent on, fixed now: the classifier is an await, and the
      // user may select another forecast before it answers.
      await classifyAndDispatch(q, active?.id ?? null);
      // setTourExamples is a stable useState setter (now a useSayerTour return);
      // eslint can't infer stability across the hook boundary, so it's deliberately
      // omitted rather than added (adding it wouldn't change re-run behavior).
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input, active, submit, pushUserMsg, pushSayerMsg, answerChat, classifyAndDispatch],
  );

  return { handleChatMessage };
}
